import type { ReactNode } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import DepthListIndent from '../Custom/DepthListIndent';
import Box from './Box';

type DepthListItemProps<T> = {
  items: readonly T[];
  index: number;
  getId: (item: T) => string;
  getParentId?: (item: T) => string | null | undefined;
  style?: StyleProp<ViewStyle>;
  children: ReactNode;
};

function computeIndentColumns<T>(
  items: readonly T[],
  getId: (item: T) => string,
  getParentId?: (item: T) => string | null | undefined,
) {
  const parentById = new Map<string, string | null>();
  const indexById = new Map<string, number>();
  const siblingLastIndexByParent = new Map<string | null, number>();

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const id = getId(item);
    const parentId = getParentId?.(item) ?? null;

    parentById.set(id, parentId);
    indexById.set(id, i);
    siblingLastIndexByParent.set(parentId, i);
  }

  const isLastSiblingById = new Map<string, boolean>();

  for (const [id, index] of indexById.entries()) {
    const parentId = parentById.get(id) ?? null;
    const lastIndex = siblingLastIndexByParent.get(parentId);
    isLastSiblingById.set(id, lastIndex === index);
  }

  const pathCache = new Map<string, string[]>();
  const visiting = new Set<string>();

  const pathFor = (id: string): string[] => {
    const cached = pathCache.get(id);
    if (cached) {
      return cached;
    }

    if (visiting.has(id)) {
      return [id];
    }

    visiting.add(id);
    const parentId = parentById.get(id);

    const path =
      parentId && parentById.has(parentId) ? [...pathFor(parentId), id] : [id];

    pathCache.set(id, path);
    visiting.delete(id);
    return path;
  };

  const columnsByIndex: boolean[][] = [];

  for (let i = 0; i < items.length; i += 1) {
    const id = getId(items[i]);
    const path = pathFor(id);
    const chain = path.length > 1 ? path.slice(1) : [];

    columnsByIndex[i] = chain.map(
      (nodeId) => !(isLastSiblingById.get(nodeId) ?? true),
    );
  }

  return columnsByIndex;
}

export default function DepthListItem<T>({
  items,
  index,
  getId,
  getParentId,
  style,
  children,
}: DepthListItemProps<T>) {
  const columnsByIndex = computeIndentColumns(items, getId, getParentId);
  const columns = columnsByIndex[index] ?? [];

  return (
    <Box direction="horizontal" alignItems="center" style={[styles.row, style]}>
      <DepthListIndent columns={columns} />
      <Box flex style={styles.content}>
        {children}
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  row: {
    height: theme.size(12),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.size(1),
    overflow: 'hidden',
  },
  content: {
    paddingLeft: theme.size(2),
  },
}));
